import React, { useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import 'react-quill/dist/quill.snow.css';
import styles from './HTMLToText.module.scss';

interface HTMLtoTextProps {
  htmlContent?: string;
  alternateMessage?: string;
  maxTextLength?: number;
}

const HTMLtoText: React.FC<HTMLtoTextProps> = ({
  htmlContent = '',
  alternateMessage = '',
  maxTextLength,
}) => {
  const { t } = useTranslation();

  const sanitizedHtml = useMemo(() => DOMPurify.sanitize(htmlContent), [htmlContent]);

  const transformLink = (node: any) => {
    if (node.type === 'tag' && node.name === 'a') {
      node.attribs.target = '_blank';
      node.attribs.rel = 'noopener noreferrer';
    }
    return node;
  };

  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitizedHtml, 'text/html');
  const stringData = (doc.body.textContent || '').trim();
  const hasImage = !!doc.querySelector('img');
  const hasContent = hasImage || !isEmpty(stringData);

  const hasLimit = typeof maxTextLength === 'number' && (maxTextLength as number) >= 0;

  const [expanded, setExpanded] = useState(false);

  const needsTruncation = hasLimit && stringData.length > (maxTextLength as number);
  const isCollapsedView = needsTruncation && !expanded;
  const truncatedText = isCollapsedView
    ? `${stringData.slice(0, maxTextLength as number)}...`
    : undefined;

  const reactElement: any = useMemo(
    () => parse(sanitizedHtml, { replace: transformLink }),
    [sanitizedHtml]
  );

  return (
    <div className={`${styles.content_wrapper}`}>
      <div className="ql-editor">
        {!hasContent ? alternateMessage : isCollapsedView ? truncatedText : reactElement}
      </div>
      {needsTruncation && (
        <span className={`view_more ${styles.toggle_link}`} onClick={() => setExpanded((v) => !v)}>
          {expanded ? t('view_less') : t('view_more')}
        </span>
      )}
    </div>
  );
};

export default HTMLtoText;
